import React, { useState, useCallback } from "react";
import classnames from "classnames";
import styles from "./DistributorConnectionTile.module.scss";
import Distributors, { Distributor } from "@src/constants/Distributors";
import { DistributorAuthMethod } from "@src/constants/DistributorAuthMethods";
import {
  DistributorConnectionLabels,
  DistributorConnectionStatus,
  DistributorConnectionStatuses,
} from "./DistributorConnectionConstants";
import {
  StatusIndicator,
  Toggle,
} from "@swordsweeper/ssi-ui-component-library";
import DistributorContactDisplay from "./DistributorContactDisplay";
import { formatCurrency } from "@src/utils/currency";
import DistributorLogo from "@src/components/common/DistributorLogo";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import Modal, { ModalSizes } from "@src/components/common/Modal";
import FormContainer from "@src/components/containers/FormContainer/FormContainer";
import DistributorConnectionForm, {
  schema as DistributorConnectionFormSchema,
  parseMidwestCustomers,
  MidwestFormStep,
} from "@src/components/forms/DistributorConnectionForm";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import * as AuthApi from "@src/api/AuthApi";
import { setPreferredDistributor } from "@src/api/DistributorApi";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { DISTRIBUTOR_NAMES } from "@src/constants/Distributors";
import omit from "lodash/omit";
import { DistributorTermsOfService } from "@src/types/termsOfService";
import { MenuController } from "@src/types/menu";
import { useCartStore } from "@src/state/cartState";
import { useCurrentUserStore } from "@src/state/currentUserState";
import find from "lodash/find";
import { DistributorApiHealthStatuses } from "@src/constants/DistributorApiHealthStatuses";
import { extractServerErrorMessage } from "@src/utils/error";

interface FormDataController {
  handleUpdate: (data: { name: string; value: any }) => void;
  setFormData: (data: any) => void;
  getFormData: () => any;
}

interface DistributorConnectionTileProps {
  distributor: Distributor;
  distributorAuthMethod: DistributorAuthMethod;
  status: DistributorConnectionStatus;
  isPreferred: boolean;
  credentialId?: string;
  amountSpentLastMonth: number;
  amountSpentYearToDate: number;
  insideSalesContactName: string;
  insideSalesContactPhone: string;
  insideSalesContactEmail: string;
  outsideSalesContactName: string;
  outsideSalesContactPhone: string;
  outsideSalesContactEmail: string;
  connectToDistributorLink: string;
  currentTerms: DistributorTermsOfService | null;
  userAgreedToTerms: boolean;
  onUpdateDistributorConnectionStatus: (status: string) => void;
  onUpdatePreferredStatus?: (isPreferred: boolean) => void;
}

export default function DistributorConnectionTile({
  distributor,
  distributorAuthMethod,
  status,
  isPreferred,
  credentialId,
  amountSpentLastMonth,
  amountSpentYearToDate,
  insideSalesContactName,
  insideSalesContactPhone,
  insideSalesContactEmail,
  outsideSalesContactName,
  outsideSalesContactPhone,
  outsideSalesContactEmail,
  connectToDistributorLink,
  currentTerms,
  userAgreedToTerms,
  onUpdateDistributorConnectionStatus,
  onUpdatePreferredStatus,
}: DistributorConnectionTileProps) {
  const { showToast } = useToast();
  const { refreshCart } = useCartStore();
  const { distributorApiHealthStatuses } = useCurrentUserStore();
  const [showDistributorConnectionForm, setShowDistributorConnectionForm] =
    useState<boolean>(false);
  const [isConnectingToDistributor, setIsConnectingToDistributor] =
    useState<boolean>(false);
  const [isUpdatingPreferred, setIsUpdatingPreferred] =
    useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [statusMenuController, setStatusMenuController] =
    useState<MenuController | null>(null);
  const [formDataController, setFormDataController] =
    useState<FormDataController | null>(null);

  const requiresTermsAgreement = Boolean(currentTerms && !userAgreedToTerms);
  const distributorDisplayName = DISTRIBUTOR_NAMES[distributor];

  // Check distributor API health status
  const backendHealthStatus: string | undefined = find(
    distributorApiHealthStatuses,
    { distributor },
  )?.status;
  const hasServiceOutage =
    backendHealthStatus === DistributorApiHealthStatuses.ScheduledMaintenance ||
    backendHealthStatus === DistributorApiHealthStatuses.ServiceOutage;

  // Get current Midwest form step from formData (used for modal sizing)
  const getMidwestStep = (): MidwestFormStep =>
    formDataController?.getFormData()?.midwestStep ??
    MidwestFormStep.ValidateCredentials;

  const handleCloseModal = useCallback(() => {
    setShowDistributorConnectionForm(false);
  }, []);

  const handleTogglePreferred = async (checked: boolean) => {
    if (!onUpdatePreferredStatus) return;

    setIsUpdatingPreferred(true);
    try {
      await setPreferredDistributor(distributor, checked);
      const action = checked ? "added to" : "removed from";
      showToast({
        message: `${DISTRIBUTOR_NAMES[distributor]} ${action} preferred distributors`,
        variant: "success",
      });
      onUpdatePreferredStatus(checked);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error updating preferred distributor",
        variant: "error",
      });
    } finally {
      setIsUpdatingPreferred(false);
    }
  };

  const handleConnectToDistributor = async (formData: any) => {
    // For Midwest, step 1 validates credentials, step 2 submits
    if (distributor === Distributors.MidwestVeterinarySupply) {
      const currentStep =
        formData.midwestStep || MidwestFormStep.ValidateCredentials;

      if (currentStep === MidwestFormStep.ValidateCredentials) {
        // Step 1: Validate credentials and get customer list
        setIsConnectingToDistributor(true);
        try {
          const result = await AuthApi.validateMidwestCredentials(
            formData.loginId,
            formData.password,
          );

          // Parse customers and validate we have selectable accounts
          let customers;
          try {
            customers = parseMidwestCustomers(result.customers);
          } catch (parseError) {
            console.error("Failed to parse Midwest customer data:", parseError);
            throw new Error(
              "Unable to load account information. Please try again.",
            );
          }

          if (customers.length === 0) {
            throw new Error(
              "No accounts found. Please contact Midwest Veterinary Supply.",
            );
          }

          // Use setFormData to batch update all fields at once
          // Clear password after successful validation for security
          if (formDataController) {
            formDataController.setFormData({
              ...formData,
              midwestStep: MidwestFormStep.SelectAccount,
              midwestRefreshToken: result.refreshToken,
              midwestCustomers: customers,
              selectedBillTo: "",
              selectedShipTo: "",
              password: "",
            });
          }
          // Note: midwestStep is set to 2 in formData above, which triggers modal resize via getMidwestStep()
        } catch (error) {
          // Extract error message from response body, consistent with other distributors
          console.error(error);
          const errorObj = error as any;
          let responseBody = errorObj?.response?.body;

          // Handle case where response body might be a stringified JSON
          if (typeof responseBody === "string") {
            try {
              responseBody = JSON.parse(responseBody);
            } catch {
              // If parsing fails, use the string as-is
            }
          }

          // Also check error.response.text in case body is not available
          if (!responseBody && errorObj?.response?.text) {
            try {
              responseBody = JSON.parse(errorObj.response.text);
            } catch {
              // If parsing fails, ignore
            }
          }

          const serverErrorMessage = extractServerErrorMessage(responseBody);
          if (
            backendHealthStatus ===
            DistributorApiHealthStatuses.ScheduledMaintenance
          ) {
            showToast({
              message: `${DISTRIBUTOR_NAMES[distributor]} is currently unavailable due to scheduled maintenance. Please try again later.`,
              variant: "error",
            });
          } else {
            // Show toast notification like other distributors
            showToast({
              message:
                serverErrorMessage ||
                `Unable to validate ${DISTRIBUTOR_NAMES[distributor]} credentials. Please verify your email and password.`,
              variant: "error",
            });
          }
        } finally {
          setIsConnectingToDistributor(false);
        }
        return;
      }

      // Step 2: Submit the connection
      setIsConnectingToDistributor(true);
      try {
        const distributorAuthCredentials = {
          distributor,
          token: formData.midwestRefreshToken,
          token_type: "refresh",
          other: {
            billTo: formData.selectedBillTo,
            shipTo: formData.selectedShipTo || "",
          },
        };

        const response = await AuthApi.setDistributorAuthCredentials(
          distributorAuthCredentials,
        );
        if (response.status === 200) {
          onUpdateDistributorConnectionStatus(
            DistributorConnectionStatuses.Connected,
          );
          showToast({
            message: `Successfully connected to ${DISTRIBUTOR_NAMES[distributor]}`,
            variant: "success",
          });
          handleCloseModal();
        }
      } catch (e) {
        console.error(e);
        const errorObj = e as any;
        const responseBody = errorObj?.response?.body;

        const serverErrorMessage = extractServerErrorMessage(responseBody);
        showToast({
          message: serverErrorMessage || "Error connecting to distributor",
          variant: "error",
        });
      } finally {
        setIsConnectingToDistributor(false);
      }
      return;
    }

    // Default handler for other distributors
    setIsConnectingToDistributor(true);
    try {
      const { username, password, ...other } = formData;
      const distributorAuthCredentials = {
        distributor,
        username,
        password,
        other: omit(other, ["distributor", "distributorAuthMethod"]),
      };

      const response = await AuthApi.setDistributorAuthCredentials(
        distributorAuthCredentials,
      );
      if (response.status === 200) {
        onUpdateDistributorConnectionStatus(
          DistributorConnectionStatuses.Connected,
        );
        showToast({
          message: `Successfully connected to ${DISTRIBUTOR_NAMES[distributor]}`,
          variant: "success",
        });
        handleCloseModal();
      }
    } catch (e) {
      console.error(e);
      const errorObj = e as any;
      const responseBody = errorObj?.response?.body;

      const serverErrorMessage = extractServerErrorMessage(responseBody);
      showToast({
        message: serverErrorMessage || "Error connecting to distributor",
        variant: "error",
      });
    } finally {
      setIsConnectingToDistributor(false);
    }
  };

  const handleRemoveConnection = async () => {
    setIsDisconnecting(true);
    try {
      await AuthApi.deleteDistributorAuthCredentials(credentialId);
      onUpdateDistributorConnectionStatus(
        DistributorConnectionStatuses.NotConnected,
      );
      // Refresh cart to remove items from disconnected distributor
      await refreshCart();
      showToast({
        message: `Disconnected from ${distributorDisplayName}`,
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error disconnecting from distributor",
        variant: "error",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRefreshConnection = async () => {
    setIsRefreshing(true);
    try {
      await AuthApi.deleteDistributorAuthCredentials(credentialId);
      onUpdateDistributorConnectionStatus(
        DistributorConnectionStatuses.NotConnected,
      );
      // Refresh cart to remove items from disconnected distributor
      await refreshCart();
      showToast({
        message: `Disconnected from ${distributorDisplayName}`,
        variant: "success",
      });
      if (distributor === Distributors.Covetrus && connectToDistributorLink) {
        window.location.assign(connectToDistributorLink);
      } else {
        setShowDistributorConnectionForm(true);
      }
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error disconnecting from distributor",
        variant: "error",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const renderConnectToDistributor = (content: React.ReactNode) => {
    const dataTestId = `connect-to-distributor-button-${distributor}`;
    if (!!connectToDistributorLink) {
      return (
        <a
          className="padding-y-sm"
          href={connectToDistributorLink}
          data-testid={dataTestId}
        >
          {content}
        </a>
      );
    }
    return (
      <Button
        className="btn btn-link"
        onClick={() => setShowDistributorConnectionForm(true)}
        data-testid={dataTestId}
      >
        <span className="text-sm">{content}</span>
      </Button>
    );
  };

  // Determine submit button text based on distributor and step
  const getSubmitButtonText = (): string => {
    if (distributor === Distributors.MidwestVeterinarySupply) {
      return getMidwestStep() === MidwestFormStep.ValidateCredentials
        ? "Validate Credentials"
        : "Submit";
    }
    return "Submit";
  };

  return (
    <div
      className={styles.root}
      data-testid={
        hasServiceOutage ? "warning-tile" : "distributor-connection-tile"
      }
    >
      <div className="flex-row space-between">
        <DistributorLogo distributorSlug={distributor} height={40} />
        {(() => {
          const indicator = (
            <StatusIndicator
              className={classnames(styles.statusIndicator, styles[status])}
              status="CUSTOM"
              customText={
                <div className={styles.statusText}>
                  <i className="fa-solid fa-circle" />{" "}
                  {DistributorConnectionLabels[status]}
                </div>
              }
            />
          );

          if (status !== DistributorConnectionStatuses.Connected) {
            return indicator;
          }

          return (
            <div className={styles.statusContainer}>
              <Menu
                buttonContent={
                  <button
                    type="button"
                    className={styles.miniStatusMenuButton}
                    aria-label={`Connection options for ${distributorDisplayName}`}
                    aria-haspopup="menu"
                  >
                    <i
                      className={classnames(
                        styles.statusText,
                        "fa-light fa-ellipsis",
                      )}
                    />
                  </button>
                }
                theme={MenuThemes.Medium}
                variations={{ noCarat: true }}
                onReceiveController={setStatusMenuController}
              >
                <MenuItem
                  data-testid={`${distributor}-refresh-connection-menu-item`}
                >
                  <div
                    className="padding-y-sm text-sm"
                    onClick={handleRefreshConnection}
                  >
                    Refresh Connection
                  </div>
                </MenuItem>
                <MenuItem
                  data-testid={`${distributor}-remove-connection-menu-item`}
                >
                  <div
                    className="padding-y-sm text-sm"
                    onClick={handleRemoveConnection}
                  >
                    Remove Connection
                  </div>
                </MenuItem>
              </Menu>
            </div>
          );
        })()}
      </div>

      {status === DistributorConnectionStatuses.Connected ? (
        <div data-testid={`${distributor}-connected-tile`}>
          <div className="flex-row spaced-content">
            <div className="flex-1">
              <DistributorContactDisplay
                label="Inside Sales"
                name={insideSalesContactName}
                phone={insideSalesContactPhone}
                email={insideSalesContactEmail}
              />
            </div>
            <div className="flex-1">
              <DistributorContactDisplay
                label="Outside Sales"
                name={outsideSalesContactName}
                phone={outsideSalesContactPhone}
                email={outsideSalesContactEmail}
              />
            </div>
          </div>
          <div className="flex-row spaced-content border-top border-bottom padding-y-md margin-y-md">
            <div className="flex-1">
              <div className="text-xs">Spent Last Month</div>
              <div className="text-lg font-weight-bold">
                {formatCurrency(amountSpentLastMonth)}
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs">Spent YTD</div>
              <div className="text-lg font-weight-bold">
                {formatCurrency(amountSpentYearToDate)}
              </div>
            </div>
          </div>
          <div className="flex-row spaced-content space-between">
            <span className="text-xs">Preferred Distributor</span>
            <div id={`preferred-distributor-toggle-${distributor}`}>
              <Toggle
                checked={isPreferred}
                onChange={handleTogglePreferred}
                disabled={isUpdatingPreferred}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classnames(styles.connectToDistributorContainer, {
            [styles.warning]: hasServiceOutage,
          })}
        >
          {hasServiceOutage && (
            <div
              className="text-center"
              data-testid={`${distributor}-unavailable-tile`}
            >
              <div className="text-md font-weight-bold text-warning">
                This distributor is currently unavailable due to{" "}
                {backendHealthStatus ===
                DistributorApiHealthStatuses.ScheduledMaintenance
                  ? "scheduled maintenance"
                  : "a service outage"}
                .
              </div>
              <div className="margin-top-xs">Please try again later.</div>
            </div>
          )}
          {renderConnectToDistributor("Connect to Distributor")}
        </div>
      )}

      {distributor !== Distributors.Covetrus && (
        <Modal
          isShowing={showDistributorConnectionForm}
          size={
            distributor === Distributors.MidwestVeterinarySupply &&
            getMidwestStep() === MidwestFormStep.SelectAccount
              ? ModalSizes.Medium
              : ModalSizes.ExtraSmall
          }
          onClose={handleCloseModal}
          closeOnBackdropClick={false}
          header={`Connect to ${distributorDisplayName}`}
        >
          <FormContainer
            form={DistributorConnectionForm}
            schema={DistributorConnectionFormSchema}
            initialData={{
              distributor,
              distributorAuthMethod,
              userAgreedToTerms,
              requiresTermsAgreement,
              midwestStep: MidwestFormStep.ValidateCredentials,
              midwestRefreshToken: "",
              midwestCustomers: [],
              selectedBillTo: "",
              selectedShipTo: "",
            }}
            additionalFormProps={{
              distributor,
              distributorAuthMethod,
              userAgreedToTerms,
              currentTerms,
            }}
            onCancel={handleCloseModal}
            onSubmit={handleConnectToDistributor}
            submitButtonText={getSubmitButtonText()}
            onReceiveFormDataController={setFormDataController}
          />

          {isConnectingToDistributor && <SpinningLoader />}
        </Modal>
      )}
    </div>
  );
}
